import * as d3 from 'd3';
import {
  visibleDatasets, domainFor, resolveDomain, clampRange, toCsv, alignZeroBaseline,
} from '@/util/chart/chartState';
import {
  subscribe, publish, HOVER, ZOOM,
} from '@/util/chart/chartSync';
import { formatDateTime } from '@/util/date';

function download(blob, name) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// State toolkit hidup lebih lama dari komponennya — chart dibongkar-pasang tiap filter di-Apply.
const stored = {};

// Cangkang tipis: state interaksi chart, keputusan diteruskan ke chartState/chartSync.
export default {
  data() {
    return {
      hiddenSeries: {},
      zoomRange: null,
      lockedY: null,
      tkUnsubs: [],
      tkOffHover: null,
    };
  },
  methods: {
    // Kartu Custom Chart punya grup sendiri biar zoom/hover-nya gak nyebar ke kartu lain.
    tkChannel(base) {
      return this.syncGroup ? `${base}:${this.syncGroup}` : base;
    },
    tkVisible(datasets) {
      return visibleDatasets(datasets, this.hiddenSeries);
    },
    tkDomain(axis, datasets, fallbackMax, extra) {
      const computed = domainFor(datasets, axis, fallbackMax);
      const extraVals = (extra || []).filter((v) => typeof v === 'number' && Number.isFinite(v));
      // Digabung sebelum kunci diterapkan, biar sumbu terkunci ga bisa ditembus puncak.
      const merge = extraVals.length
        ? [Math.min(computed[0], ...extraVals), Math.max(computed[1], ...extraVals)]
        : computed;
      return resolveDomain(this.lockedY ? this.lockedY[axis] : null, merge);
    },
    // Nyamain posisi garis nol antar sumbu. Sumbu yang dikunci dilewatin —
    // kalau ikut digeser, ngunci sumbu jadi gak ada artinya.
    tkAlignZero(domains) {
      if (this.lockedY) return domains;
      return alignZeroBaseline(domains);
    },
    tkXDomain(timestamps) {
      const full = d3.extent(timestamps);
      if (!this.zoomRange) return full;
      const clamped = clampRange(this.zoomRange, [+full[0], +full[1]]);
      if (!clamped) {
        // Rentang zoom lama ga nyentuh data baru, jadi dibuang.
        this.zoomRange = null;
        return full;
      }
      return [new Date(clamped[0]), new Date(clamped[1])];
    },
    tkToggleSeries(name) {
      this.$set(this.hiddenSeries, name, !this.hiddenSeries[name]);
      this.tkSave();
      this.drawChart();
    },
    tkToggleLockY(domains) {
      this.lockedY = this.lockedY ? null : domains;
      this.tkSave();
      this.drawChart();
    },
    tkInstallBrush(svg, x, width, height) {
      const brush = d3.brushX().extent([[0, 0], [width, height]])
        .on('end', (event) => {
          // Brush.move(null) di bawah numbuk event end lagi, jadi tanpa cek ini rekursi ga abis-abis.
          if (!event.selection) return;
          const [a, b] = event.selection.map((px) => +x.invert(px));
          svg.select('.tk-brush').call(brush.move, null);
          publish(this.tkChannel(ZOOM), [a, b]);
        });
      const g = svg.append('g').attr('class', 'tk-brush').call(brush)
        .on('dblclick', () => publish(this.tkChannel(ZOOM), null));
      // Hover nempel di overlay brush; rect sendiri bakal ketutup dan tooltipnya mati.
      return g.select('.overlay');
    },
    tkPublishHover(ms) {
      publish(this.tkChannel(HOVER), ms);
    },
    tkPublishZoom(range) {
      publish(this.tkChannel(ZOOM), range);
    },
    // Dipanggil tiap gambar ulang, jadi yang lama wajib dilepas dulu.
    tkOnHover(fn) {
      if (this.tkOffHover) this.tkOffHover();
      this.tkOffHover = subscribe(this.tkChannel(HOVER), fn);
    },
    tkStateKey() {
      const base = this.$options.name || 'chart';
      const route = this.$route ? (this.$route.name || this.$route.path || '') : '';
      const ship = this.$store ? this.$store.getters['filter/currentMassId'] : '';
      const part = [base, route || '-', ship || '-'];
      if (this.type) part.push(this.type);
      if (this.tkKeyExtra) part.push(this.tkKeyExtra);
      return part.join(':');
    },
    tkSave() {
      stored[this.tkStateKey()] = {
        hiddenSeries: this.hiddenSeries,
        lockedY: this.lockedY,
      };
    },
    tkBind() {
      const saved = stored[this.tkStateKey()];
      if (saved) {
        this.hiddenSeries = { ...saved.hiddenSeries };
        this.lockedY = saved.lockedY;
      }
      // Handler ZOOM cuma set state + gambar ulang, gak publish ZOOM lagi.
      this.tkUnsubs.push(subscribe(this.tkChannel(ZOOM), (range) => {
        if (this._isDestroyed) return;
        this.zoomRange = range;
        this.tkSave();
        this.drawChart();
      }));
    },
    tkUnbind() {
      if (this.tkOffHover) this.tkOffHover();
      this.tkOffHover = null;
      this.tkUnsubs.forEach((off) => off());
      this.tkUnsubs = [];
    },
    // Nama berkas ikut rentang yang keliatan dan kerincian yang dipakai.
    tkFileName(base) {
      let range = this.zoomRange;
      let granularity = '';
      const own = typeof this.tkFileRange === 'function' ? this.tkFileRange() : null;
      if (own) {
        if (!range) range = [own[0], own[1]];
        granularity = String(own[2] || '').toLowerCase();
      } else {
        try {
          const g = this.$store ? this.$store.getters : null;
          if (g) {
            const date = g['filter/date'];
            if (!range && date && date.start && date.end) range = [date.start, date.end];
            granularity = String(g['filter/interval'] || '').toLowerCase();
          }
        } catch (e) {
          range = this.zoomRange;
        }
      }
      if (!range) return base;
      const cap = (v) => formatDateTime(v, 'YYYYMMDD-HHmm', null, false);
      const part = [base, cap(range[0]), cap(range[1])];
      if (granularity) part.push(granularity);
      return part.join('_');
    },
    tkExportCsv(fileName, timestamps, datasets, formatTs = (ms) => formatDateTime(ms, 'YYYY-MM-DD HH:mm:ss')) {
      const content = toCsv({
        timestamps: timestamps.map((t) => +t),
        datasets,
        formatTs,
      });
      download(new Blob([content], { type: 'text/csv;charset=utf-8' }), `${this.tkFileName(fileName)}.csv`);
    },
    tkExportPng(fileName) {
      const svg = this.$refs.chartContainer.querySelector('svg');
      if (!svg) return;
      const box = svg.getBoundingClientRect();
      const text = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = box.width * 2;
        canvas.height = box.height * 2;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => download(blob, `${this.tkFileName(fileName)}.png`));
      };
      img.onerror = () => console.warn('Gagal export PNG chart');
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
    },
  },
};
